import React from 'react'

import {noop} from 'es-toolkit'
import {isEmpty, pull} from 'es-toolkit/compat'

import './select-field.scss'

import useKeyPress from '../../hooks/use-key-press'
import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

import arrowDropDownIcon from '../../assets/icons/arrow-drop-down.svg'
import chevronDownIcon from '../../assets/icons/chevron-down.svg'

import Icon from '../icon'
import PopoverHandler from '../popover-handler'
import {ChildrenRenderProps} from '../popover-handler/PopoverHandler'
import SelectMenu from '../select-menu'

export type Value<ValueType, AdditionalData extends Record<string, unknown>> = {
  value: ValueType
  label?: string // When not provided value will be shown
} & AdditionalData

export type BaseProps<
  ValueType = unknown,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
> = Overwrite<
  CommonProps<'button'>,
  {
    name?: string
    placeholder?: string
    menu: React.ReactElement<React.ComponentPropsWithRef<typeof SelectMenu>>
    value?: Value<ValueType, AdditionalData> | Value<ValueType, AdditionalData>[]
    icon?: React.ReactElement
    size?: '90' | '100' | '200'
    appearance?: 'base' | 'error'
    variant?: 'base' | 'inline' | 'inlinePill'
    mixed?: boolean
    mixedLabel?: string
    disabled?: boolean
    onChange?:
      | ((value: Value<ValueType, AdditionalData>, name: string | undefined) => void)
      | ((value: Value<ValueType, AdditionalData>[], name: string | undefined) => void)
    onBlur?: React.FocusEventHandler
  }
>

const getLabel = (label?: string, value?: unknown) => label || `${value}`

export const BaseSelectField = <
  ValueType,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
>({
  className,
  name,
  placeholder = '',
  menu,
  value,
  icon,
  size = '100',
  appearance = 'base',
  variant = 'base',
  mixed = false,
  mixedLabel,
  disabled = false,
  onChange = noop,
  onBlur = noop,
  ...props
}: BaseProps<ValueType, AdditionalData>) => {
  const menuRef = React.useRef<SelectMenu>(null)

  const hasMultipleValues = (
    v: BaseProps<ValueType, AdditionalData>['value']
  ): v is Value<ValueType, AdditionalData>[] => Array.isArray(v)
  const hasMultipleValuesOnChange = (
    v: BaseProps<ValueType, AdditionalData>['value'],
    changeFn: BaseProps<ValueType, AdditionalData>['onChange']
  ): changeFn is (value: Value<ValueType, AdditionalData>[], name: string | undefined) => void =>
    Array.isArray(v)

  useKeyPress(event => {
    if (menuRef.current && menuRef.current.handleKeyDown) {
      menuRef.current.handleKeyDown(event.key)
    }
  })

  const renderButton = (renderProps?: ChildrenRenderProps<HTMLButtonElement, HTMLDivElement>) => {
    const {ref, isOpen, open, close} = renderProps ?? {
      isOpen: false,
      open: noop,
      close: noop,
    }

    return (
      <button
        {...props}
        ref={ref}
        type="button"
        name={name}
        className={cn(
          'SelectField',
          {
            [`size-${size}`]: !!size,
            [`appearance-${appearance}`]: !!appearance,
            [`variant-${variant}`]: !!variant,
            open: isOpen,
            mixed,
            disabled,
          },
          className
        )}
        onBlur={event => {
          event.target.value = `${value}`
          event.target.name = `${name}`
          onBlur(event)
        }}
        onClick={isOpen ? close : open}
        disabled={!menu || disabled}
      >
        <span className="SelectField__value">
          {icon && <span className="SelectField__icon">{icon}</span>}
          {!mixed && isEmpty(value) && (
            <span className="SelectField__placeholder">{placeholder}</span>
          )}
          {(!isEmpty(value) || mixed) && (
            <span className="SelectField__label">
              {mixed
                ? mixedLabel
                : hasMultipleValues(value)
                  ? value.map(v => getLabel(v.label, v.value)).join(', ')
                  : value
                    ? getLabel(value.label, value.value)
                    : ''}
            </span>
          )}
        </span>
        <Icon source={variant === 'inline' ? arrowDropDownIcon : chevronDownIcon} />
      </button>
    )
  }

  return (
    <PopoverHandler
      popover={({ref, style, anchorRect, close}) => (
        <div
          ref={ref}
          className="SelectField__menu"
          style={{
            ...style,
            minWidth: anchorRect?.width,
          }}
        >
          {React.cloneElement(menu, {
            ref: menuRef,
            onClick: (v: unknown) => {
              const typedValue = v as Value<ValueType, AdditionalData>

              if (hasMultipleValues(value) && hasMultipleValuesOnChange(value, onChange)) {
                if (value.includes(typedValue)) {
                  onChange(pull([...value], typedValue), name)
                } else {
                  onChange([...value, typedValue], name)
                }
              }

              if (!hasMultipleValues(value) && !hasMultipleValuesOnChange(value, onChange)) {
                onChange(typedValue, name)
                close()
              }
            },
            selectedValue: value,
          })}
        </div>
      )}
    >
      {renderButton}
    </PopoverHandler>
  )
}

export interface Props<
  ValueType = unknown,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
> extends Omit<BaseProps<ValueType, AdditionalData>, 'value' | 'onChange'> {
  value?: Value<ValueType, AdditionalData>
  onChange?: (value: Value<ValueType, AdditionalData>, name: string | undefined) => void
}

function SelectField<
  ValueType = unknown,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
>(props: Props<ValueType, AdditionalData>) {
  return <BaseSelectField<ValueType, AdditionalData> {...props} />
}

export default SelectField
